import React, { useCallback, useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Play, Pause, RotateCcw, Clock } from 'lucide-react';
import { useTimeTracking } from '@/context/TimeTrackingContext';

interface TimeTrackerProps {
  taskId: string;

}

export const TimeTracker: React.FC<TimeTrackerProps> = ({
  taskId,

}) => {
  const { startTimer, pauseTimer, resetTimer, getTaskTime, setTaskTime } = useTimeTracking();
  const [, setForceUpdate] = useState(0);
  const [isEditing, setIsEditing] = useState(false);
  const [editHours, setEditHours] = useState('');
  const [editMinutes, setEditMinutes] = useState('');

  // Force re-render every second when timer is running
  useEffect(() => {
    const timer = getTaskTime(taskId);
    if (!timer.isRunning) return;

    const interval = setInterval(() => {
      setForceUpdate(prev => prev + 1);
    }, 1000);

    return () => clearInterval(interval);
  }, [taskId, getTaskTime]);

  const formatTime = useCallback((totalSeconds: number) => {
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    if (hours > 0) {
      return `${hours.toString().padStart(2, '0')} hr ${minutes.toString().padStart(2, '0')} mins ${seconds.toString().padStart(2, '0')} secs`;
    } else if (minutes > 0) {
      return `${minutes.toString().padStart(2, '0')} mins ${seconds.toString().padStart(2, '0')} secs`;
    } else {
      return `${seconds.toString().padStart(2, '0')} secs`;
    }
  }, []);

  const timer = getTaskTime(taskId);

  const handleStartStop = () => {
    if (timer.isRunning) {
      pauseTimer(taskId);
    } else {
      startTimer(taskId);
    }
  };

  const handleReset = () => {
    resetTimer(taskId);
  };

  const handleEditClick = () => {
    const hours = Math.floor(timer.totalSeconds / 3600);
    const minutes = Math.floor((timer.totalSeconds % 3600) / 60);
    setEditHours(hours.toString());
    setEditMinutes(minutes.toString());
    setIsEditing(true);
  };

  const handleSaveEdit = () => {
    const hours = parseInt(editHours) || 0;
    const minutes = parseInt(editMinutes) || 0;
    const newTotalSeconds = (hours * 3600) + (minutes * 60);
    setIsEditing(false);
    // Update the timer with the new time
    setTaskTime(taskId, newTotalSeconds);
  };

  return (
    <div className="flex items-center gap-2 text-sm text-foreground">
      <Clock className="h-4 w-4 text-muted-foreground" />
      <span className="text-muted-foreground text-sm whitespace-nowrap">
        Time Spent
      </span>

      {isEditing ? (
        <div className="flex items-center gap-2">
          <input
            type="number"
            min="0"
            value={editHours}
            onChange={(e) => setEditHours(e.target.value)}
            className="w-16 h-7 text-sm px-2 rounded bg-background border border-input text-foreground"
            placeholder="Hours"
          />
          <span>hr</span>
          <input
            type="number"
            min="0"
            max="59"
            value={editMinutes}
            onChange={(e) => setEditMinutes(e.target.value)}
            className="w-16 h-7 text-sm px-2 rounded bg-background border border-input text-foreground"
            placeholder="Mins"
          />
          <span>mins</span>
                     <Button
             size="sm"
             onClick={handleSaveEdit}
             className="h-7 px-2 bg-workspace-accent hover:bg-workspace-accent-hover text-white"
           >
             Save
           </Button>
        </div>
      ) : (
        <>
                     <span
             onClick={handleEditClick}
             className={`text-sm cursor-pointer hover:underline ${
               timer.isRunning ? 'text-destructive' : 'text-foreground'
             }`}
           >
             {formatTime(timer.totalSeconds)}
           </span>

           <div className="flex items-center gap-1">
             <Button
               size="sm"
               variant="ghost"
               onClick={handleStartStop}
               className={`h-7 w-7 p-0 ${
                 timer.isRunning
                   ? 'text-destructive hover:bg-destructive/10'
                   : 'hover:bg-muted text-muted-foreground hover:text-foreground'
               }`}
             >
              {timer.isRunning ? (
                <Pause className="h-4 w-4" />
              ) : (
                <Play className="h-4 w-4" />
              )}
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={handleReset}
              className="h-7 w-7 p-0 hover:bg-muted text-muted-foreground hover:text-foreground"
            >
              <RotateCcw className="h-4 w-4" />
            </Button>
          </div>
        </>
      )}
    </div>
  );
};